import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mic, Square, Upload, Trash2, Play, Pause, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AudioRecorder — records from the microphone (MediaRecorder API) OR uploads
 * an audio file, stores it via /api/upload, and exposes {path,title,duration}
 * entries to the parent through value / onChange.
 */
export const AudioRecorder = ({ value = [], onChange }) => {
  const { API, token } = useAuth();
  const { t } = useLang();
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const uploadBlob = async (blob, ext, title, duration) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], `audio.${ext}`, { type: blob.type || `audio/${ext}` }));
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const rec = {
        path: data.path,
        title: title || new Date().toLocaleString(),
        duration_seconds: duration,
        uploaded_at: new Date().toISOString(),
      };
      onChange([...(value || []), rec]);
      toast.success(t("uploadAudio") + " ✓");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
        stream.getTracks().forEach(tr => tr.stop());
        const dur = elapsed;
        await uploadBlob(blob, "webm", "", dur);
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } catch (err) {
      toast.error(t("micDenied"));
    }
  };

  const stopRecording = () => {
    if (mediaRecRef.current && mediaRecRef.current.state !== "inactive") {
      mediaRecRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.includes(".") ? file.name.split(".").pop() : "mp3";
    await uploadBlob(file, ext, file.name.replace(/\.[^.]+$/, ""), null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAt = (idx) => {
    const next = [...(value || [])];
    next.splice(idx, 1);
    onChange(next);
  };

  const updateTitle = (idx, title) => {
    const next = [...(value || [])];
    next[idx] = { ...next[idx], title };
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="audio-recorder">
      <div className="flex items-center gap-2 flex-wrap">
        {!recording ? (
          <Button type="button" onClick={startRecording} variant="outline" className="border-[#993D22] text-[#993D22]" data-testid="start-record-btn">
            <Mic className="w-4 h-4 mr-2" /> {t("recordAudio")}
          </Button>
        ) : (
          <Button type="button" onClick={stopRecording} className="bg-red-700 hover:bg-red-800 text-white" data-testid="stop-record-btn">
            <Square className="w-4 h-4 mr-2" /> {t("stopRecording")} ({elapsed}s)
          </Button>
        )}
        <input ref={fileRef} type="file" accept="audio/*" onChange={onFile} className="hidden" data-testid="audio-file-input" />
        <Button type="button" onClick={() => fileRef.current?.click()} variant="outline" className="border-[#6B4226] text-[#6B4226]" disabled={uploading} data-testid="upload-audio-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          {t("uploadAudio")}
        </Button>
      </div>

      {(value || []).length === 0 ? (
        <div className="text-sm italic text-[#6E5B4F]">{t("noAudio")}</div>
      ) : (
        <ul className="space-y-2">
          {value.map((a, idx) => (
            <li key={a.path} className="rounded-lg border border-[#E3D5CA] bg-[#FAF7F2] p-3 flex flex-col sm:flex-row gap-3 sm:items-center" data-testid={`audio-item-${idx}`}>
              <Input
                value={a.title || ""}
                onChange={(e) => updateTitle(idx, e.target.value)}
                placeholder={t("audioTitle")}
                className="flex-1 bg-white"
              />
              <AudioPlayer path={a.path} token={token} api={API} />
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-red-700 hover:text-red-800"
                onClick={() => removeAt(idx)}
                data-testid={`remove-audio-${idx}`}
                title={t("deleteAudio")}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/** Streaming audio player with authorized blob fetch. */
export const AudioPlayer = ({ path, token: tokenProp, api: apiProp }) => {
  const auth = useAuth();
  const token = tokenProp ?? auth?.token;
  const api = apiProp ?? auth?.API;
  const [src, setSrc] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => () => { if (src) URL.revokeObjectURL(src); }, [src]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${api}/files/${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setSrc(url);
      setTimeout(() => { audioRef.current?.play(); setPlaying(true); }, 60);
    } catch (_) {
      toast.error("Playback failed");
    } finally { setLoading(false); }
  };

  const toggle = () => {
    if (!src) { load(); return; }
    if (playing) { audioRef.current?.pause(); setPlaying(false); }
    else { audioRef.current?.play(); setPlaying(true); }
  };

  return (
    <div className="flex items-center gap-2">
      <Button type="button" size="sm" onClick={toggle} className="btn-outline-terra !py-1.5 !px-3" data-testid={`play-audio-${path}`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
      </Button>
      {src && <audio ref={audioRef} src={src} onEnded={() => setPlaying(false)} className="h-8" controls preload="none" />}
    </div>
  );
};

export default AudioRecorder;
